import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, ChevronDown, Shield, Cloud, Users, 
  FileText, Briefcase, Phone, Building2, TestTube2, 
  CreditCard, ClipboardList
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

const serviceLinks = [
  { name: 'Services Overview', href: '/services', icon: Briefcase },
  { name: 'PolicyCenter', href: '/services/policycenter', icon: Shield },
  { name: 'ClaimCenter', href: '/services/claimcenter', icon: ClipboardList },
  { name: 'BillingCenter', href: '/services/billingcenter', icon: CreditCard },
  { name: 'Cloud & Modernisation', href: '/cloud-modernisation', icon: Cloud },
  { name: 'QA & Testing', href: '/qa-testing', icon: TestTube2 },
  { name: 'Staffing & Talent', href: '/staffing', icon: Users },
];

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'Resources', href: '/resources' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-slate-900/95 backdrop-blur-lg shadow-sm border-b border-slate-800' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center gap-2 group"
          >
            <img 
              src="/guidepro-logo.png" 
              alt="GuidePro Inc." 
              className="h-14 w-auto object-contain"
              style={{ minWidth: '160px' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                location.pathname === '/' 
                  ? 'text-sky-400 bg-sky-900/30' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                location.pathname === '/about' 
                  ? 'text-sky-400 bg-sky-900/30' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              About
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="services-dropdown-trigger"
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    location.pathname.includes('/services') || 
                    location.pathname.includes('/cloud') ||
                    location.pathname.includes('/qa') ||
                    location.pathname.includes('/staffing')
                      ? 'text-sky-400 bg-sky-900/30' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  Services <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className="w-64 p-2 bg-slate-900/95 backdrop-blur-lg border border-slate-800 shadow-xl"
                data-testid="services-dropdown-content"
              >
                {serviceLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link
                      to={link.href}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800 cursor-pointer transition-colors duration-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-sky-900/50 flex items-center justify-center">
                        <link.icon className="w-4 h-4 text-sky-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-200">{link.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/case-studies"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                location.pathname === '/case-studies' 
                  ? 'text-sky-400 bg-sky-900/30' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Case Studies
            </Link>

            <Link
              to="/resources"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                location.pathname === '/resources' 
                  ? 'text-sky-400 bg-sky-900/30' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Resources
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact">
              <Button 
                variant="ghost" 
                className="text-slate-300 hover:text-sky-400"
                data-testid="contact-nav-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/20"
                data-testid="request-demo-btn"
              >
                Request Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-slate-300 hover:bg-slate-800"
              data-testid="mobile-menu-btn"
              type="button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-900 border-t border-slate-800"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium ${
                    location.pathname === link.href
                      ? 'text-sky-400 bg-sky-900/30'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="pt-4 border-t border-slate-800">
                <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Services
                </p>
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800"
                  >
                    <link.icon className="w-4 h-4 text-sky-400" />
                    {link.name}
                  </Link>
                ))}
              </div>

              <div className="pt-4 space-y-2">
                <Link to="/contact" className="block">
                  <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    Request Demo
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
